
<template>
  <router-view></router-view>
</template>

<script>
import firebaseLib from "firebase";
import "firebase/database";
import firebase from "./assets/config/firebase";
import fetchAllTrelloData from "./store/modules/complains"
import _ from "lodash";
import { requestNotificationPermission } from "../src/services/notificationService";

export default {
  data() {
    return {
      isImpersonating: false
    };
  },
  async mounted() {
    const company = this.$store.state.auth.company;
    const user = this.$store.state.auth.user;
    const currentPath = this.$router.history.current.path;

    try {
      // if firebase isn't configured
      if (!firebase.checkAlreadyConfiguredApp() && company) {
        await firebase.configureFirebase(company.firebase); //configure firebase
        await this.$store.dispatch('fetchAllTrelloData');
        if (user && user.uid) {
          this.watchUserStatusRealTime(user.uid); // ✅ ADDED LINE
        }
        this.fetchAllUsersRealTime();
        this.fetchConstantsRealTime();
        this.fetchNewUsersRealTime();
        this.removeUserRealTime();
        this.fetchLeavesRealTime()
        this.fetchCurrentUser()
        this.fetchUserLeavesRealTime()
      }
    } catch (e) {
      console.log("e =>", e);
    }
  },
   beforeDestroy() {
    if (this.userListenerRef) {
      this.userListenerRef.off();
      this.userListenerRef = null;
    }
  },
  methods: {
    watchUserStatusRealTime(uid) {
      this.userListenerRef = firebaseLib.database().ref(`users/${uid}`);
      this.userListenerRef.on("value", (snapshot) => {
        const userData = snapshot.val();
        if (userData && userData.isActive === false) {
          this.$store.commit("setUser", null);
          this.$store.commit("setCompany", null);
          this.$store.commit("setConstants", {});
          this.$router.replace("/");
          localStorage.clear();
          if (this.userListenerRef) {
            this.userListenerRef.off();
            this.userListenerRef = null;
          }
        }
      });
    },

    fetchConstantsRealTime() {
      const constants = this.$store.state.auth.constants || {};
      firebaseLib
        .database()
        .ref("constants")
        .on("child_changed", data => {
          if (data.val()) {
            constants[data.key] = data.val();
            this.$store.commit("setConstants", constants);
          }
        });
    },
    fetchNewUsersRealTime() {
      const allUsers = this.$store.state.auth.allUsers;
      firebaseLib
        .database()
        .ref("users")
        .on("child_added", data => {
          if (data.val()) {
            const foundUser = _.find(allUsers, { uid: data.key });
            if (!foundUser) {
              allUsers.push({ ...data.val(), uid: data.key });
              this.$store.commit("setAllUsers", allUsers);
            }
          }
        });
    },
    removeUserRealTime() {
      const allUsers = this.$store.state.auth.allUsers;
      firebaseLib
        .database()
        .ref("users")
        .on("child_removed", data => {
          if (data.val()) {
            const foundIndex = _.findIndex(allUsers, { uid: data.key });
            if (foundIndex > -1) {
              allUsers.splice(foundIndex, 1);
              this.$store.commit("setAllUsers", allUsers);
            }
          }
        });
    },
    fetchAllUsersRealTime() {
      firebaseLib
        .database()
        .ref("users")
        .on("child_changed", data => {
          if (data.val()) {
            const allUsers = this.$store.state.auth.allUsers;
            const targetIndex = _.findIndex(allUsers, { uid: data.key });
            if (targetIndex !== -1) {
              allUsers[targetIndex] = { ...data.val(), uid: data.key };
              this.$store.commit("setAllUsers", [...allUsers]);
            }
          }
        });
    },
    async fetchCurrentUser() {
    const user = this.$store.state.auth.user;
    if (user && user.uid) {
      try {
        const userSnapshot = await firebaseLib
          .database()
          .ref(`users/${user.uid}`)
          .once("value");
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          this.$store.commit("setUser", {
            uid: user.uid,
            ...userData,
          });
        }
      } catch (error) {
        console.error("Error fetching current user data:", error);
      }
    }
  },
    fetchLeavesRealTime() {
    firebaseLib
      .database()
      .ref("leaves")
      .orderByChild("createdAt")
      .on("value", (snapshot) => {
        const leaves = [];
        snapshot.forEach((childSnapshot) => {
          const leave = childSnapshot.val();
          leave.id = childSnapshot.key;
          leaves.push(leave);
        });
        const sortedLeaves = leaves.reverse();
        this.$store.commit("setAllLeaves", sortedLeaves);
      }, (error) => {
        console.error("Error fetching real-time leaves: ", error);
      });
  },

  fetchUserLeavesRealTime() {
  const user = this.$store.state.auth.user; 

  if (!user) {
    console.error("User not found!");
    return;
  }

  firebaseLib
    .database()
    .ref("leaves")
    .orderByChild("userId")
    .equalTo(user.uid)
    .on("value", (snapshot) => {
      const leaves = [];
      snapshot.forEach((childSnapshot) => {
        const leave = childSnapshot.val();
        leave.id = childSnapshot.key;
        leaves.push(leave);
      });
      const sortedLeaves = leaves.sort((a, b) => b.createdAt - a.createdAt);
      this.$store.commit("setUserLeaves", sortedLeaves);

    }, (error) => {
      console.error("Error fetching real-time leaves: ", error);
    });
    },
    async initializeAppData() {
      try {
        const user = this.$store.state.auth.user;
        if (!user || !user.uid) {
          console.warn("No user found in store");
          return;
        }

        // Check if user is active
        const userSnapshot = await firebaseLib.database().ref(`users/${user.uid}`).once("value");
        const userData = userSnapshot.val();

        if (!userData || userData.isActive === false) {
          console.warn("User is not active");
          // Handle inactive user (logout, redirect, etc.)
          this.$store.commit("setUser", null);
          this.$store.commit("setCompany", null);
          this.$store.commit("setConstants", {});
          this.$router.replace("/");
          return;
        }

        // Fetch all necessary data in parallel
        const [allUsersSnapshot, constantsSnapshot, leavesSnapshot, errorsSnapshot] = await Promise.all([
          firebaseLib.database().ref("users").once("value"),
          firebaseLib.database().ref("constants").once("value"),
          firebaseLib.database().ref("leaves").once("value"),
          firebaseLib.database().ref("errors").once("value")
        ]);

        // Process users data
        const users = [];
        allUsersSnapshot.forEach(childSnapshot => {
          users.push({ ...childSnapshot.val(), uid: childSnapshot.key });
        });
        this.$store.commit("setAllUsers", users);
        this.$store.commit("setConstants", constantsSnapshot.val() || {});
        const leaves = [];
        leavesSnapshot.forEach(childSnapshot => {
          leaves.push({ ...childSnapshot.val(), id: childSnapshot.key });
        });
        this.$store.commit("setAllLeaves", leaves.reverse());
        // just for console the errors collection on refresh, it will be used in future for error tracking and management
        const errors = [];
        errorsSnapshot.forEach((childSnapshot) => {
          const errorData = childSnapshot.val();
          errorData.id = childSnapshot.key;       
          errors.push(errorData);
        });
        console.log("Errors Collection Fetched on Refresh:", errors);

        // Fetch Trello data
        await this.$store.dispatch('fetchAllTrelloData');

        // Set up realtime listeners
        this.fetchAllUsersRealTime();
        this.fetchLeavesRealTime();
        this.fetchUserLeavesRealTime();
        this.fetchConstantsRealTime();
        this.fetchNewUsersRealTime();
        this.fetchCurrentUser();
        this.removeUserRealTime();
      } catch (e) {
        console.error("Initialization error:", e);
      }
    }
  },
  async created() {
    this.isImpersonating = !!localStorage.getItem('impersonateUser');
    const company = this.$store.state.auth.company;
    try {
      if (!firebaseLib.apps || firebaseLib.apps.length === 0) {
        await firebaseLib.initializeApp(company.firebase);
        await this.initializeAppData();
      }
    } catch (e) {
      console.error("Firebase initialization error:", e);
    }

    await requestNotificationPermission();
  }

};
</script>

<style lang="scss"></style>



// WEBPACK FOOTER //
// src/App.vue